import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Package, Search, Filter, Trash2, ShoppingCart, Edit, ChevronDown, ChevronRight } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import toast from 'react-hot-toast';
import api, { componentsAPI, componentTypesAPI, purchasesAPI, caseRemovalsAPI, uploadsAPI, cartridgeAbbrev, cartridgesStatic } from '../services/api';
import ImagePreviewModal from '../components/ImagePreviewModal';
import PdfThumbnail from '../components/PdfThumbnail';
import { recipesAPI } from '../services/api';
import { formatLocalDate } from '../services/date';

const Components = () => {
  const [components, setComponents] = useState([]);
  const [componentTypes, setComponentTypes] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [caseBatches, setCaseBatches] = useState({});
  const [bulletOptions, setBulletOptions] = useState({
    core_material: [],
    covering: [],
    shape: [],
    cartridge: []
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filters, setFilters] = useState({
    brass: { brand: [], head_stamp: [], caliber: [], primer_pocket_size: [], primer_pocket_type: [] },
    bullet: { brand: [], model: [], weight: [], core_material: [], covering: [], shape: [], caliber: [] },
    primer: { brand: [], model: [], firearm_type: [], primer_size: [], magnum: [] },
    powder: { brand: [], model: [] }
  });
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [sortBy, setSortBy] = useState({ brass: 'remaining', bullet: 'remaining', primer: 'remaining', powder: 'remaining' });
  const [showFilters, setShowFilters] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [modalEditing, setModalEditing] = useState(false);
  const [removals, setRemovals] = useState([]);
  const [removalForm, setRemovalForm] = useState({ case_batch_id: '', quantity: '', reason: '', removal_date: '' });
  const [images, setImages] = useState([]);
  const [imagesByComponent, setImagesByComponent] = useState({});
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '', starting_firing_count: '0' });
  const [filePreview, setFilePreview] = useState(null);
  const [modalSectionsCollapsed, setModalSectionsCollapsed] = useState({ purchases: true, recipes: true });
  const navigate = useNavigate();
  // Brass cartridge "Other" controls (create & edit)
  const [showBrassOther, setShowBrassOther] = useState(false);
  const [brassOtherValue, setBrassOtherValue] = useState('');
  const [showBrassOtherEdit, setShowBrassOtherEdit] = useState(false);
  const [brassOtherValueEdit, setBrassOtherValueEdit] = useState('');
  
  const { register, handleSubmit, reset, formState: { errors }, control, getValues, setValue } = useForm();
  const selectedComponentType = useWatch({ control, name: 'component_type_id' });
  
  // Watch all form fields that are used conditionally
  const cannelured = useWatch({ control, name: 'cannelured' });
  const coreMaterial = useWatch({ control, name: 'core_material' });
  const covering = useWatch({ control, name: 'covering' });
  const shape = useWatch({ control, name: 'shape' });
  const customCartridge = useWatch({ control, name: 'custom_cartridge' });
  const intendedCartridges = useWatch({ control, name: 'intended_cartridges' }) || [];
  const [showIntendedDropdown, setShowIntendedDropdown] = useState(false);
  const intendedRef = useRef(null);
  const [secondaryFilter, setSecondaryFilter] = useState('');
  const primerPocketSize = useWatch({ control, name: 'primer_pocket_size' });
  const [collapsedCategories, setCollapsedCategories] = useState({ brass: true, bullet: true, primer: true, powder: true });
  const hasAutoCollapsedRef = useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (intendedRef.current && !intendedRef.current.contains(e.target)) {
        setShowIntendedDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const toggleIntended = (val) => {
    const current = Array.isArray(intendedCartridges) ? intendedCartridges : [];
    if (current.includes(val)) {
      setValue('intended_cartridges', current.filter(v => v !== val));
    } else {
      setValue('intended_cartridges', [...current, val]);
    }
  };

  const handlePurchaseCalc = (field, value) => {
    const quantity = parseFloat(field === 'quantity' ? value : purchaseForm.quantity) || 0;
    let next = { ...purchaseForm, [field]: value };
    if (field === 'total_cost' || field === 'quantity') {
      const calcUnit = quantity > 0 ? ( (parseFloat(next.total_cost)||0) / quantity ) : 0;
      next.unit_cost = isFinite(calcUnit) ? calcUnit.toFixed(2) : '0.00';
    }
    if (field === 'unit_cost' || field === 'quantity') {
      const calcTotal = (parseFloat(next.unit_cost)||0) * quantity;
      next.total_cost = isFinite(calcTotal) ? calcTotal.toFixed(2) : '0.00';
    }
    setPurchaseForm(next);
  };

  const submitPurchaseFromComponent = async () => {
    try {
      if (!selectedComponent) return;
      const payload = {
        component_id: selectedComponent.id,
        quantity: parseFloat(purchaseForm.quantity) || 0,
        unit: selectedComponent.component_type?.name?.toLowerCase() === 'powder' ? 'pounds' : 'pieces',
        unit_cost: parseFloat(purchaseForm.unit_cost) || 0,
        total_cost: parseFloat(purchaseForm.total_cost) || 0,
        purchase_date: purchaseForm.purchase_date,
        purchase_location: purchaseForm.purchase_location || '',
        website: purchaseForm.website || '',
        notes: purchaseForm.notes || '',
        starting_firing_count: selectedComponent.component_type?.name?.toLowerCase() === 'brass' ? (parseInt(purchaseForm.starting_firing_count) || 0) : undefined
      };
      await purchasesAPI.create(payload);
      toast.success('Purchase recorded');
      setShowPurchaseModal(false);
      // reload purchases/case batches for this component
      loadData();
    } catch (e) {
      toast.error('Failed to record purchase');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [componentsRes, typesRes, purchasesRes, recipesRes] = await Promise.all([
        componentsAPI.getAll(),
        componentTypesAPI.getAll(),
        purchasesAPI.getAll(),
        recipesAPI.getAll(),
      ]);
      setComponents(componentsRes.data);
      setComponentTypes(typesRes.data);
      setPurchases(purchasesRes.data);
      setRecipes(recipesRes.data);
      
      // Load case batches for brass components
      const brassComponents = componentsRes.data.filter(c => c.component_type.name.toLowerCase() === 'brass');
      const caseBatchPromises = brassComponents.map(component => 
        componentsAPI.getCaseBatches(component.id).then(res => ({ componentId: component.id, data: res.data }))
      );
      
      try {
        const caseBatchResults = await Promise.all(caseBatchPromises);
        const newCaseBatches = {};
        caseBatchResults.forEach(result => {
          newCaseBatches[result.componentId] = result.data;
        });
        setCaseBatches(newCaseBatches);
      } catch (caseBatchError) {
        console.error('Error loading case batches:', caseBatchError);
        // Don't fail the entire load if case batches fail
      }
      
      // Load bullet options
      const optionTypes = ['core_material', 'covering', 'shape', 'cartridge'];
      const optionsPromises = optionTypes.map(type => 
        api.get(`/bullet-options/${type}`).then(res => res.data)
      );
      const optionsResults = await Promise.all(optionsPromises);
      
      const newBulletOptions = {};
      optionTypes.forEach((type, index) => {
        newBulletOptions[type] = optionsResults[index];
      });
      // Merge cartridge options: static + custom from backend
      const staticList = cartridgesStatic();
      const normalize = (v) => (v || '').trim().replace(/^\./, '').trim();
      const staticSet = new Set(staticList.map(s => (s.value || '').trim().toLowerCase()));
      const dynamicList = (newBulletOptions['cartridge'] || [])
        .map(o => ({ id: o.id, value: normalize(o.value) }))
        .filter(o => o.value && !o.value.startsWith('.'));
      const seen = new Set();
      const merged = [];
      [...staticList, ...dynamicList].forEach(o => {
        const key = normalize(o.value).toLowerCase();
        if (!key || seen.has(key)) return;
        seen.add(key);
        if (staticSet.has(key)) {
          const found = staticList.find(s => (s.value || '').trim().toLowerCase() === key);
          if (found) { merged.push(found); return; }
        }
        merged.push({ id: o.id, value: normalize(o.value) });
      });
      merged.sort((a,b)=> (a.value||'').localeCompare(b.value||'', undefined, {sensitivity:'base', numeric:true}));
      newBulletOptions['cartridge'] = merged;
      setBulletOptions(newBulletOptions);

      // Load uploaded files per component to show thumbnails on cards
      try {
        const imagePromises = (componentsRes.data || []).map(c =>
          uploadsAPI.list('component', c.id)
            .then(res => ({ componentId: c.id, files: Array.isArray(res.data) ? res.data : [] }))
            .catch(() => ({ componentId: c.id, files: [] }))
        );
        const imageResults = await Promise.all(imagePromises);
        const imagesMap = {};
        imageResults.forEach(({ componentId, files }) => { imagesMap[componentId] = files; });
        setImagesByComponent(imagesMap);
      } catch (e) {
        // Non-fatal if uploads fail to load
        setImagesByComponent({});
      }
    } catch (error) {
      console.error('Error loading components:', error);
      toast.error('Failed to load components');
    } finally {
      setLoading(false);
      // Auto-collapse only on initial load
      if (!hasAutoCollapsedRef.current) {
        hasAutoCollapsedRef.current = true;
        setCollapsedCategories({ brass: true, bullet: true, primer: true, powder: true });
      }
    }
  };

  const toggleCategory = (type) => {
    setCollapsedCategories(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const collapseAllCategories = () => {
    setCollapsedCategories({ brass: true, bullet: true, primer: true, powder: true });
  };

  const expandAllCategories = () => {
    setCollapsedCategories({ brass: false, bullet: false, primer: false, powder: false });
  };

  const addBulletOption = async (optionType, value) => {
    try {
      let v = String(value || '').trim();
      if (optionType === 'cartridge' && v.startsWith('.')) v = v.slice(1).trim();
      const { data } = await api.post('/bullet-options', { option_type: optionType, value: v });
      return data;
    } catch (e) {
      toast.error('Failed to add option');
      return null;
    }
  };

  const addBrassCustomCartridge = async (isEdit = false) => {
    const raw = isEdit ? brassOtherValueEdit : brassOtherValue;
    let name = String(raw || '').trim();
    if (!name) return;
    // Reuse bullet-options endpoint to persist custom cartridge
    const newOption = await addBulletOption('cartridge', name);
    if (newOption) {
      // merge into local options immediately for UX
      setBulletOptions(prev => {
        const list = prev.cartridge || [];
        if (!list.find(o => (o.value || '').toLowerCase() === newOption.value.toLowerCase())) {
          return { ...prev, cartridge: [...list, { id: newOption.id, value: newOption.value }] };
        }
        return prev;
      });
      setValue('caliber', newOption.value);
      if (isEdit) {
        setBrassOtherValueEdit('');
        setShowBrassOtherEdit(false);
      } else {
        setBrassOtherValue('');
        setShowBrassOther(false);
      }
      toast.success('Custom cartridge added');
    }
  };

  const onSubmit = async (data) => {
    try {
      const selectedType = componentTypes.find(t => t.id === parseInt(selectedComponentType));
      const typeName = selectedType?.name.toLowerCase();
      
      // Handle component-specific data based on type
      if (typeName === 'brass') {
        // For brass, keep head_stamp explicitly; set model only if empty for backward compatibility
        if (!data.model && data.head_stamp) {
        data.model = data.head_stamp;
        }
      } else if (typeName === 'primer') {
        // For primer, no caliber field needed
        data.caliber = `${data.firearm_type} ${data.primer_size}${data.magnum ? ' Magnum' : ''}`;
      } else if (typeName === 'powder') {
        // For powder, no caliber field
        data.caliber = '';
      } else if (typeName === 'bullet') {
        // Handle bullet-specific data
        if (data.intended_cartridges) {
          data.intended_cartridges = JSON.stringify(data.intended_cartridges);
        }
        
        // Handle "other" options
        if (data.core_material === 'other' && data.core_material_other) {
          const newOption = await addBulletOption('core_material', data.core_material_other);
          if (newOption) {
            data.core_material = data.core_material_other;
          }
        }
        
        if (data.covering === 'other' && data.covering_other) {
          const newOption = await addBulletOption('covering', data.covering_other);
          if (newOption) {
            data.covering = data.covering_other;
          }
        }
        
        if (data.shape === 'other' && data.shape_other) {
          const newOption = await addBulletOption('shape', data.shape_other);
          if (newOption) {
            data.shape = data.shape_other;
          }
        }
      }
      
      if (editingComponent) {
        // Update existing component
        await componentsAPI.update(editingComponent.id, data);
        toast.success('Component updated successfully');
        setEditingComponent(null);
        if (modalEditing) {
          setModalEditing(false);
          // Refresh components and selected details
          await loadData();
          if (selectedComponent) {
            const updated = (components.find(c => c.id === selectedComponent.id) || selectedComponent);
            setSelectedComponent(updated);
          }
        }
      } else {
        // Create new component
        await componentsAPI.create(data);
        toast.success('Component created successfully');
      }
      
      if (modalEditing) {
        // keep modal open
      } else {
      setShowForm(false);
      }
      reset();
      loadData();
    } catch (error) {
      console.error('Error saving component:', error);
      const backendMsg = error?.response?.data?.message || error?.response?.data?.error;
      const prefix = editingComponent ? 'Failed to update component' : 'Failed to add component';
      toast.error(backendMsg ? `${prefix}: ${backendMsg}` : prefix);
    }
  };

  const handleEdit = (component) => {
    setEditingComponent(component);
    setShowForm(true);
    
    // Pre-populate form with component data
    const formData = {
      component_type_id: component.component_type.id.toString(),
      brand: component.brand,
      model: component.model,
      caliber: component.caliber,
      description: component.description,
      head_stamp: component.head_stamp || '',
    };
    
    // Add component-specific fields
    const typeName = component.component_type.name.toLowerCase();
    if (typeName === 'brass') {
      formData.head_stamp = component.head_stamp;
      formData.primer_pocket_size = component.primer_pocket_size || '';
      formData.primer_pocket_type = component.primer_pocket_type || '';
    } else if (typeName === 'primer') {
      formData.firearm_type = component.firearm_type;
      formData.primer_size = component.primer_size;
      formData.magnum = component.magnum;
    } else if (typeName === 'bullet') {
      formData.weight_grains = component.weight_grains;
      formData.length_inches = component.length_inches;
      formData.caliber_inches = component.caliber_inches;
      formData.cannelured = component.cannelured;
      formData.cannelure_depth = component.cannelure_depth;
      formData.core_material = component.core_material;
      formData.core_material_other = component.core_material_other;
      formData.covering = component.covering;
      formData.covering_other = component.covering_other;
      formData.shape = component.shape;
      formData.shape_other = component.shape_other;
      formData.intended_cartridges = component.intended_cartridges ? JSON.parse(component.intended_cartridges) : [];
    }
    
    reset(formData);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingComponent(null);
    try { reset({}); } catch {}
  };

  const matchesFilter = (component, componentType, filterKey, filterValue) => {
    // filterValue is now an array; if empty, match all
    if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0) return true;
    
    const values = filterValue.map(v => v.toLowerCase());
    switch (filterKey) {
      case 'brand':
        return values.includes(component.brand.toLowerCase());
      case 'model':
        return component.model && values.includes(component.model.toLowerCase());
      case 'caliber':
        return values.includes(component.caliber.toLowerCase());
      case 'head_stamp':
        return component.head_stamp && values.includes(component.head_stamp.toLowerCase());
      case 'weight':
        return component.weight_grains && values.includes(component.weight_grains.toString());
      case 'primer_pocket_size':
        return values.includes((component.primer_pocket_size || '').toLowerCase());
      case 'primer_pocket_type':
        return values.includes((component.primer_pocket_type || '').toLowerCase());
      case 'core_material':
        return component.core_material && values.includes(component.core_material.toLowerCase());
      case 'covering':
        return component.covering && values.includes(component.covering.toLowerCase());
      case 'shape':
        return component.shape && values.includes(component.shape.toLowerCase());
      case 'firearm_type':
        return component.firearm_type && values.includes(component.firearm_type.toLowerCase());
      case 'primer_size':
        return component.primer_size && values.includes(component.primer_size.toLowerCase());
      case 'magnum':
        return values.includes(String(component.magnum));
      default:
        return true;
    }
  };

  const filterComponents = (components, componentType) => {
    const typeFilters = filters[componentType];
    return components.filter(component => {
      // Basic search
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      component.brand.toLowerCase().includes(searchLower) ||
      component.model.toLowerCase().includes(searchLower) ||
      component.caliber.toLowerCase().includes(searchLower) ||
      (component.head_stamp && component.head_stamp.toLowerCase().includes(searchLower)) ||
      (component.firearm_type && component.firearm_type.toLowerCase().includes(searchLower)) ||
      (component.primer_size && component.primer_size.toLowerCase().includes(searchLower));
    
      // Type filter
    const matchesType = !filterType || component.component_type.id.toString() === filterType;
    
      // Component-specific filters
      const matchesFilters = Object.entries(typeFilters).every(([key, value]) => 
        matchesFilter(component, componentType, key, value)
      );
      
      return matchesSearch && matchesType && matchesFilters;
    });
  };

  const sortComponents = (components, componentType) => {
    const sortField = sortBy[componentType];
    return [...components].sort((a, b) => {
      const statsA = getComponentStats(a.id);
      const statsB = getComponentStats(b.id);
      
      switch (sortField) {
        case 'remaining':
          return statsB.totalRemaining - statsA.totalRemaining;
        case 'total_purchased':
          return statsB.totalQuantity - statsA.totalQuantity;
        case 'total_spent':
          return statsB.totalSpent - statsA.totalSpent;
        case 'brand':
          return a.brand.localeCompare(b.brand);
        case 'caliber':
          return a.caliber.localeCompare(b.caliber);
        default:
          return 0;
      }
    });
  };

  const getComponentsByType = (componentType) => {
    const typeComponents = components.filter(c => 
      c.component_type.name.toLowerCase() === componentType
    );
    const filtered = filterComponents(typeComponents, componentType);
    return sortComponents(filtered, componentType);
  };

  const handleFilterChange = (componentType, key, value) => {
    // value is now an array
    setFilters(prev => ({
      ...prev,
      [componentType]: {
        ...prev[componentType],
        [key]: value
      }
    }));
  };

  const toggleFilterValue = (componentType, key, value) => {
    setFilters(prev => {
      const currentValues = prev[componentType][key] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [componentType]: {
          ...prev[componentType],
          [key]: newValues
        }
      };
    });
  };

  const clearFilters = (componentType) => {
    setFilters(prev => ({
      ...prev,
      [componentType]: Object.keys(prev[componentType]).reduce((acc, key) => {
        acc[key] = [];
        return acc;
      }, {})
    }));
  };

  const handleSortChange = (componentType, field) => {
    setSortBy(prev => ({
      ...prev,
      [componentType]: field
    }));
  };

  const getActiveFilterCount = (componentType) => {
    return Object.values(filters[componentType]).filter(value => Array.isArray(value) && value.length > 0).length;
  };

  // Get a component's value for a given field
  const getComponentFieldValue = (component, field) => {
    switch (field) {
      case 'brand':
        return component.brand;
      case 'model':
        return component.model;
      case 'caliber':
        return component.caliber;
      case 'head_stamp':
        return component.head_stamp;
      case 'weight':
        return component.weight_grains ? component.weight_grains.toString() : null;
      case 'primer_pocket_size':
        return component.primer_pocket_size;
      case 'primer_pocket_type':
        return component.primer_pocket_type;
      case 'core_material':
        return component.core_material;
      case 'covering':
        return component.covering;
      case 'shape':
        return component.shape;
      case 'firearm_type':
        return component.firearm_type;
      case 'primer_size':
        return component.primer_size;
      case 'magnum':
        return component.magnum !== undefined ? String(component.magnum) : null;
      default:
        return null;
    }
  };

  // Check if a component matches all other filters in its type (excluding the current field)
  const componentMatchesOtherFilters = (component, componentType, excludeField) => {
    const typeFilters = filters[componentType] || {};
    return Object.entries(typeFilters).every(([field, selectedValues]) => {
      if (field === excludeField) return true; // Skip the field we're getting options for
      if (!selectedValues || selectedValues.length === 0) return true; // No filter applied
      
      const componentValue = getComponentFieldValue(component, field);
      if (!componentValue) return false;
      
      return selectedValues.map(v => v.toLowerCase()).includes(componentValue.toString().toLowerCase());
    });
  };

  // Get unique values for a field from components of a specific type, filtered by other selections
  const getUniqueValues = (componentType, field) => {
    const typeComponents = components.filter(c => 
      c.component_type.name.toLowerCase() === componentType
    );
    const values = new Set();
    typeComponents.forEach(c => {
      // Check if this component matches other filters in the same type
      if (!componentMatchesOtherFilters(c, componentType, field)) {
        return; // Skip this component
      }
      
      const val = getComponentFieldValue(c, field);
      if (val && val.toString().trim()) {
        values.add(val.toString().trim());
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
  };

  // Dropdown checkbox filter component
  const FilterDropdown = ({ componentType, field, label, options }) => {
    const dropdownKey = `${componentType}-${field}`;
    const isOpen = openFilterDropdown === dropdownKey;
    const selectedValues = filters[componentType][field] || [];
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          if (openFilterDropdown === dropdownKey) {
            setOpenFilterDropdown(null);
          }
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownKey]);

    if (options.length === 0) return null;

    const handleToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.currentTarget) {
        e.currentTarget.blur();
      }
      setOpenFilterDropdown(isOpen ? null : dropdownKey);
    };

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          tabIndex={-1}
          className={`input text-sm w-full text-left flex items-center justify-between ${selectedValues.length > 0 ? 'border-blue-500 bg-blue-50' : ''}`}
          onMouseDown={handleToggle}
          onClick={(e) => e.preventDefault()}
        >
          <span className={selectedValues.length > 0 ? 'text-blue-700' : 'text-gray-500'}>
            {selectedValues.length > 0 ? `${label} (${selectedValues.length})` : label}
          </span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {options.map(option => {
              const displayValue = field === 'magnum' ? (option === 'true' ? 'Magnum' : 'Standard') : option;
              return (
                <label
                  key={option}
                  className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    tabIndex={-1}
                    checked={selectedValues.includes(option)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleFilterValue(componentType, field, option);
                    }}
                    className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="truncate">{displayValue}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Legacy filter removed (unused)

  const getTypeIcon = (typeName) => {
    switch (typeName.toLowerCase()) {
      case 'brass':
        return '🥉';
      case 'primer':
        return '🔥';
      case 'bullet':
        return '🎯';
      case 'powder':
        return '💨';
      default:
        return '📦';
    }
  };

  const getTypeColor = (typeName) => {
    switch (typeName.toLowerCase()) {
      case 'brass':
        return 'bg-yellow-100 text-yellow-800';
      case 'primer':
        return 'bg-red-100 text-red-800';
      case 'bullet':
        return 'bg-blue-100 text-blue-800';
      case 'powder':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Abbreviate multi-word or hyphenated labels (e.g., "Semi-Jacketed" -> "SJ", "Full Metal Jacket" -> "FMJ")
  const abbreviateMultiWord = (value) => {
    if (!value) return '';
    const str = String(value).trim();
    const parts = str.split(/[\s-]+/).filter(Boolean);
    if (parts.length <= 1) return str; // single token; leave as-is
    return parts.map(p => (p[0] || '').toUpperCase()).join('');
  };

  const getComponentStats = (componentId) => {
    const component = components.find(c => c.id === componentId);
    const componentPurchases = purchases.filter(p => p.component.id === componentId);
    
    const totalQuantity = componentPurchases.reduce((sum, p) => sum + Number(p.quantity || 0), 0);
    const totalSpent = componentPurchases.reduce((sum, p) => sum + Number(p.total_cost || 0), 0);
    let totalRemaining = componentPurchases.reduce((sum, p) => sum + Number(p.remaining_quantity || 0), 0);

    if ((component?.component_type?.name || '').toLowerCase() === 'brass') {
      totalRemaining = (caseBatches[componentId] || []).reduce((sum, batch) => sum + Number(batch.remaining_quantity || 0), 0);
    }
    
    return {
      totalQuantity,
      totalSpent,
      totalRemaining,
      hasPurchases: componentPurchases.length > 0
    };
  };

const sumCaseBatchQuantities = (caseBatches, componentId) => {
  const map = {};
  (caseBatches[componentId] || []).forEach(batch => {
    const fc = Number(batch.firing_count || 0);
    map[fc] = (map[fc] || 0) + Number(batch.remaining_quantity || 0);
  });
  return map;
};

const buildPurchaseTotalsByFiringCount = (purchases, componentId) => {
  const totals = {};
  purchases
    .filter(p => p.component.id === componentId)
    .forEach(p => {
      const fc = Number(p.starting_firing_count || 0);
      if (!totals[fc]) totals[fc] = { qty: 0, cost: 0 };
      totals[fc].qty += Number(p.quantity || 0);
      totals[fc].cost += Number(p.quantity || 0) * Number(p.unit_cost || 0);
    });
  return totals;
};

const computeBrassAmortization = (componentId, purchases, caseBatches, components) => {
  const component = components.find(c => c.id === componentId);
  if (!component || (component.component_type?.name || '').toLowerCase() !== 'brass') {
    return {};
  }
  const fcQuantities = sumCaseBatchQuantities(caseBatches, componentId);
  const purchaseTotals = buildPurchaseTotalsByFiringCount(purchases, componentId);
  const fcSet = new Set([
    ...Object.keys(fcQuantities).map(Number),
    ...Object.keys(purchaseTotals).map(Number),
  ]);
  if (fcSet.size === 0) return {};
  
  // Simple algorithm:
  // 1. Base cost per case = total purchase cost / total cases purchased (all purchases)
  // 2. For each firing count: firingsLeft = max(0, round(baseCost / 0.01) - firingCount)
  // 3. avgCost = max(0, baseCost - (firingCount × 0.01))
  
  let totalPurchaseCost = 0;
  let totalPurchaseQty = 0;
  Object.values(purchaseTotals).forEach(info => {
    totalPurchaseCost += info.cost || 0;
    totalPurchaseQty += info.qty || 0;
  });
  
  const baseCostPerCase = totalPurchaseQty > 0 ? totalPurchaseCost / totalPurchaseQty : 0;
  const totalAmortizationFirings = Math.round(baseCostPerCase / 0.01);
  
  const amortMap = {};
  const sortedFcs = Array.from(fcSet).sort((a, b) => a - b);
  
  sortedFcs.forEach(fc => {
    // Calculate amortization for all firing counts (even if 0 cases remaining)
    // so the UI can display correct values
    
    // Remaining cost = base cost - (firings already done × $0.01)
    const avgCost = Math.max(0, baseCostPerCase - (fc * 0.01));
    
    // Firings left = total amortization firings - firings already done
    const firingsLeft = Math.max(0, totalAmortizationFirings - fc);
    
    amortMap[fc] = {
      avgCost,
      firingsLeft,
      isAmortized: firingsLeft === 0,
      baseCostPerCase, // Include for reference
      totalAmortizationFirings, // Include for reference
    };
  });
  
  return amortMap;
};

  const openComponentModal = (component) => {
    setSelectedComponent(component);
    setShowComponentModal(true);
    setModalEditing(false);
    // Load removals for brass components
    if (component.component_type?.name?.toLowerCase() === 'brass') {
      componentsAPI.getCaseRemovals(component.id)
        .then(res => setRemovals(res.data))
        .catch(() => setRemovals([]));
    } else {
      setRemovals([]);
    }
    // Load images
    uploadsAPI.list('component', component.id).then(res => setImages(res.data)).catch(() => setImages([]));
  };

  const closeComponentModal = () => {
    setSelectedComponent(null);
    setShowComponentModal(false);
    setModalEditing(false);
    try { reset({}); } catch {}
  };

  const getPurchasesForComponent = (componentId) => {
    return purchases.filter(p => p.component?.id === componentId);
  };

  const getRecipesUsingComponent = (componentId) => {
    return recipes.filter(r => Array.isArray(r.components) && r.components.some(rc => rc.component?.id === componentId));
  };

  const canDeleteComponent = (component) => {
    const hasPurchases = getPurchasesForComponent(component.id).length > 0;
    const usedInRecipes = getRecipesUsingComponent(component.id).length > 0;
    const isBrass = (component.component_type?.name || '').toLowerCase() === 'brass';
    const hasBatches = isBrass && Array.isArray(caseBatches[component.id]) && caseBatches[component.id].length > 0;
    return !hasPurchases && !usedInRecipes && !hasBatches;
  };

  const deleteComponent = async (component) => {
    if (!canDeleteComponent(component)) {
      toast.error('Cannot delete: component has purchases, is used in recipes, or has case batches');
      return;
    }
    if (!window.confirm('Delete this component? This cannot be undone.')) return;
    try {
      await componentsAPI.remove(component.id);
      toast.success('Component deleted');
      // Refresh lists
      loadData();
      // If modal open on this component, close it
      if (selectedComponent && selectedComponent.id === component.id) {
        closeComponentModal();
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to delete component');
    }
  };

  const handleRemovalChange = (field, value) => {
    setRemovalForm(prev => ({ ...prev, [field]: value }));
  };

  const submitRemoval = async () => {
    if (!removalForm.case_batch_id || !removalForm.quantity) return;
    try {
      await caseRemovalsAPI.create({
        case_batch_id: Number(removalForm.case_batch_id),
        quantity: Number(removalForm.quantity),
        reason: removalForm.reason || '',
        removal_date: removalForm.removal_date || undefined,
      });
      toast.success('Cases removed from circulation');
      // Refresh case batches and removals
      if (selectedComponent) {
        componentsAPI.getCaseBatches(selectedComponent.id).then(res => {
          setCaseBatches(prev => ({ ...prev, [selectedComponent.id]: res.data }));
        });
        componentsAPI.getCaseRemovals(selectedComponent.id).then(res => setRemovals(res.data));
      }
      setRemovalForm({ case_batch_id: '', quantity: '', reason: '', removal_date: '' });
    } catch (e) {
      toast.error('Failed to remove cases');
    }
  };

  const handleUpload = async (e) => {
    if (!selectedComponent) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    try {
      await uploadsAPI.upload('component', selectedComponent.id, files);
      const res = await uploadsAPI.list('component', selectedComponent.id);
      setImages(res.data);
      setImagesByComponent(prev => ({ ...prev, [selectedComponent.id]: Array.isArray(res.data) ? res.data : [] }));
      toast.success('Images uploaded');
    } catch (err) {
      toast.error('Failed to upload images');
    } finally {
      // reset input
      e.target.value = '';
    }
  };

  const removeImage = async (imageId) => {
    try {
      await uploadsAPI.remove(imageId);
      setImages(prev => prev.filter(i => i.id !== imageId));
      // Also update thumbnails on the corresponding card immediately
      setImagesByComponent(prev => {
        const list = Array.isArray(prev[selectedComponent.id]) ? prev[selectedComponent.id] : [];
        return { ...prev, [selectedComponent.id]: list.filter(i => i.id !== imageId) };
      });
      toast.success('Image deleted');
    } catch {
      toast.error('Failed to delete image');
    }
  };

  // Start edit inside modal
  const startModalEdit = (component) => {
    setEditingComponent(component);
    // Pre-fill form values for edit
    setValue('component_type_id', component.component_type.id);
    setValue('brand', component.brand);
    setValue('model', component.model);
    setValue('caliber', component.caliber || '');
    setValue('description', component.description || '');
    // Type-specific fields
    if (component.component_type.name.toLowerCase() === 'brass') {
      setValue('head_stamp', component.head_stamp || '');
      setValue('primer_pocket_size', component.primer_pocket_size || '');
      setValue('primer_pocket_type', component.primer_pocket_type || '');
    } else if (component.component_type.name.toLowerCase() === 'primer') {
      setValue('firearm_type', component.firearm_type || '');
      setValue('primer_size', component.primer_size || '');
      setValue('magnum', component.magnum || false);
    } else if (component.component_type.name.toLowerCase() === 'bullet') {
      setValue('weight_grains', component.weight_grains || '');
      setValue('length_inches', component.length_inches || '');
      setValue('caliber_inches', component.caliber_inches || '');
      setValue('cannelured', component.cannelured || false);
      setValue('cannelure_depth', component.cannelure_depth || '');
      setValue('core_material', component.core_material || '');
      setValue('covering', component.covering || '');
      setValue('shape', component.shape || '');
      try {
        setValue('intended_cartridges', component.intended_cartridges ? JSON.parse(component.intended_cartridges) : []);
      } catch (e) {
        setValue('intended_cartridges', []);
      }
    }
    setModalEditing(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Components</h1>
          <p className="text-gray-600 mt-2">Manage your reloading components</p>
        </div>
        <button
          onClick={() => { setEditingComponent(null); try { reset({}); } catch {}; setShowForm(true); }}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>Add Component</span>
        </button>
      </div>

      {/* Filters and Search */}
      <div className="flex justify-between items-center">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="input"
        >
          <option value="">All Types</option>
          {componentTypes.map(type => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn btn-secondary flex items-center space-x-2"
        >
          <Filter className="h-4 w-4" />
          <span>Advanced Filters & Search</span>
          {(getActiveFilterCount('brass') + getActiveFilterCount('bullet') + getActiveFilterCount('primer') + getActiveFilterCount('powder') > 0 || searchTerm) && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {getActiveFilterCount('brass') + getActiveFilterCount('bullet') + getActiveFilterCount('primer') + getActiveFilterCount('powder') + (searchTerm ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="card p-6">
          {/* Search Control */}
          <div className="mb-6">
            <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search components..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Brass Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                🥉 Brass Filters
                {getActiveFilterCount('brass') > 0 && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {getActiveFilterCount('brass')}
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                <FilterDropdown componentType="brass" field="brand" label="Brand" options={getUniqueValues('brass', 'brand')} />
                <FilterDropdown componentType="brass" field="head_stamp" label="Head Stamp" options={getUniqueValues('brass', 'head_stamp')} />
                <FilterDropdown componentType="brass" field="caliber" label="Cartridge" options={getUniqueValues('brass', 'caliber')} />
                <FilterDropdown componentType="brass" field="primer_pocket_size" label="Pocket Size" options={getUniqueValues('brass', 'primer_pocket_size')} />
                <FilterDropdown componentType="brass" field="primer_pocket_type" label="Pocket Type" options={getUniqueValues('brass', 'primer_pocket_type')} />
                <button
                  onClick={() => clearFilters('brass')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Bullet Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                🎯 Bullet Filters
                {getActiveFilterCount('bullet') > 0 && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {getActiveFilterCount('bullet')}
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                <FilterDropdown componentType="bullet" field="brand" label="Brand" options={getUniqueValues('bullet', 'brand')} />
                <FilterDropdown componentType="bullet" field="model" label="Model" options={getUniqueValues('bullet', 'model')} />
                <FilterDropdown componentType="bullet" field="weight" label="Weight (gr)" options={getUniqueValues('bullet', 'weight')} />
                <FilterDropdown componentType="bullet" field="core_material" label="Core Material" options={getUniqueValues('bullet', 'core_material')} />
                <FilterDropdown componentType="bullet" field="covering" label="Covering" options={getUniqueValues('bullet', 'covering')} />
                <FilterDropdown componentType="bullet" field="shape" label="Shape" options={getUniqueValues('bullet', 'shape')} />
                <FilterDropdown componentType="bullet" field="caliber" label="Caliber" options={getUniqueValues('bullet', 'caliber')} />
                <button
                  onClick={() => clearFilters('bullet')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Primer Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                🔥 Primer Filters
                {getActiveFilterCount('primer') > 0 && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {getActiveFilterCount('primer')}
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                <FilterDropdown componentType="primer" field="brand" label="Brand" options={getUniqueValues('primer', 'brand')} />
                <FilterDropdown componentType="primer" field="model" label="Model" options={getUniqueValues('primer', 'model')} />
                <FilterDropdown componentType="primer" field="firearm_type" label="Firearm Type" options={getUniqueValues('primer', 'firearm_type')} />
                <FilterDropdown componentType="primer" field="primer_size" label="Size" options={getUniqueValues('primer', 'primer_size')} />
                <FilterDropdown componentType="primer" field="magnum" label="Type" options={getUniqueValues('primer', 'magnum')} />
                <button
                  onClick={() => clearFilters('primer')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Powder Filters */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                💨 Powder Filters
                {getActiveFilterCount('powder') > 0 && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    {getActiveFilterCount('powder')}
                  </span>
                )}
              </h3>
              <div className="space-y-2">
                <FilterDropdown componentType="powder" field="brand" label="Brand" options={getUniqueValues('powder', 'brand')} />
                <FilterDropdown componentType="powder" field="model" label="Model" options={getUniqueValues('powder', 'model')} />
                <button
                  onClick={() => clearFilters('powder')}
                  className="btn btn-sm btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Component Form */}
      {showForm && (
        <div className="card p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">
            {editingComponent ? `Edit ${editingComponent.brand} ${editingComponent.component_type?.name?.toLowerCase() === 'brass' ? editingComponent.caliber : editingComponent.model}` : 'Add New Component'}
          </h2>
          
          <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Component Type</label>
              <select
                {...register('component_type_id', { required: 'Component type is required' })}
                className="input"
              >
                <option value="">Select type...</option>
                {componentTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
              {errors.component_type_id && (
                <p className="text-red-500 text-sm mt-1">{errors.component_type_id.message}</p>
              )}
            </div>

            <div>
              <label className="label">Brand</label>
              <input
                type="text"
                {...register('brand', { required: 'Brand is required' })}
                className="input"
                placeholder="e.g., Winchester"
              />
              {errors.brand && (
                <p className="text-red-500 text-sm mt-1">{errors.brand.message}</p>
              )}
            </div>

            {/* Brass-specific: Head Stamp instead of Model */}
            {(() => {
              const isBrass = selectedComponentType && componentTypes.find(t => t.id === parseInt(selectedComponentType))?.name.toLowerCase() === 'brass';
              return isBrass ? (
                <div>
                  <label className="label">Head Stamp</label>
                  <input
                    type="text"
                    {...register('head_stamp', { required: 'Head stamp is required' })}
                    className="input"
                    placeholder="e.g., WIN 357 MAG"
                  />
                  {errors.head_stamp && (
                    <p className="text-red-500 text-sm mt-1">{errors.head_stamp.message}</p>
                  )}
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Primer Pocket Size</label>
                      <select {...register('primer_pocket_size', { required: 'Primer pocket size is required' })} className="input">
                        <option value="">Select size...</option>
                        <option value="Small">Small</option>
                        <option value="Large">Large</option>
                      </select>
                      {errors.primer_pocket_size && (
                        <p className="text-red-500 text-sm mt-1">{errors.primer_pocket_size.message}</p>
                      )}
                    </div>
                    {primerPocketSize === 'Large' && (
                      <div>
                        <label className="label">Large Pocket Type</label>
                        <select {...register('primer_pocket_type')} className="input">
                          <option value="">Select type...</option>
                          <option value="Pistol">Pistol</option>
                          <option value="Rifle">Rifle</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="label">Model</label>
                  <input
                    type="text"
                    {...register('model', { required: 'Model is required' })}
                    className="input"
                    placeholder="e.g., 231"
                  />
                  {errors.model && (
                    <p className="text-red-500 text-sm mt-1">{errors.model.message}</p>
                  )}
                </div>
              );
            })()}

            {/* Caliber/Cartridge field - for brass use static cartridge dropdown, others use text */}
            {(() => {
              const typeName = selectedComponentType && componentTypes.find(t => t.id === parseInt(selectedComponentType))?.name.toLowerCase();
              if (!typeName || typeName === 'powder' || typeName === 'primer' || typeName === 'bullet') return null;
              if (typeName === 'brass') {
                return (
              <div>
                    <label className="label">Cartridge</label>
                    <div className="flex gap-2 items-start">
                      <select {...register('caliber', { required: 'Cartridge is required' })} className="input flex-1">
                        <option value="">Select cartridge...</option>
                        {bulletOptions.cartridge.map(option => (
                          <option key={option.id} value={option.value}>{option.value}</option>
                        ))}
                      </select>
                      {!showBrassOther ? (
                        <button type="button" className="btn btn-secondary" onClick={() => setShowBrassOther(true)}>Other...</button>
                      ) : (
                        <div className="flex gap-2 flex-1">
                          <input className="input flex-1" placeholder="Enter custom cartridge" value={brassOtherValue} onChange={(e) => setBrassOtherValue(e.target.value)} />
                          <button type="button" className="btn btn-secondary" onClick={() => addBrassCustomCartridge(false)}>Add</button>
                        </div>
                      )}
                    </div>
                {errors.caliber && (
                  <p className="text-red-500 text-sm mt-1">{errors.caliber.message}</p>
                )}
              </div>
                );
              }
              return (
                <div>
                  <label className="label">Caliber</label>
                  <input type="text" {...register('caliber', { required: 'Caliber is required' })} className="input" placeholder="e.g., .357 Magnum" />
                  {errors.caliber && (<p className="text-red-500 text-sm mt-1">{errors.caliber.message}</p>)}
                </div>
              );
            })()}

            <div className="md:col-span-2">
              <label className="label">Description</label>
              <textarea
                {...register('description')}
                className="input"
                rows="3"
                placeholder="Additional details about this component..."
              />
            </div>

            {/* Primer-specific fields */}
            {(() => {
              const isPrimer = selectedComponentType && componentTypes.find(t => t.id === parseInt(selectedComponentType))?.name.toLowerCase() === 'primer';
              return isPrimer ? (
                <>
                  <div>
                    <label className="label">Firearm Type</label>
                    <select
                      {...register('firearm_type', { required: 'Firearm type is required' })}
                      className="input"
                    >
                      <option value="">Select firearm type...</option>
                      <option value="Pistol">Pistol</option>
                      <option value="Rifle">Rifle</option>
                    </select>
                    {errors.firearm_type && (
                      <p className="text-red-500 text-sm mt-1">{errors.firearm_type.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="label">Size</label>
                    <select
                      {...register('primer_size', { required: 'Primer size is required' })}
                      className="input"
                    >
                      <option value="">Select size...</option>
                      <option value="Large">Large</option>
                      <option value="Small">Small</option>
                    </select>
                    {errors.primer_size && (
                      <p className="text-red-500 text-sm mt-1">{errors.primer_size.message}</p>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('magnum')}
                      className="checkbox"
                    />
                    <label className="label">Magnum</label>
                  </div>
                </>
              ) : null;
            })()}

            {/* Bullet-specific fields */}
            {selectedComponentType && componentTypes.find(t => t.id === parseInt(selectedComponentType))?.name.toLowerCase() === 'bullet' && (
              <>
                <div>
                  <label className="label">Weight (grains)</label>
                  <input
                    type="number"
                    step="0.1"
                    {...register('weight_grains', { required: 'Weight is required' })}
                    className="input"
                    placeholder="e.g., 158.0"
                  />
                  {errors.weight_grains && (<p className="text-red-500 text-sm mt-1">{errors.weight_grains.message}</p>)}
                </div>

                <div>
                  <label className="label">Length (inches)</label>
                  <input
                    type="number"
                    step="0.001"
                    {...register('length_inches', { required: 'Length is required' })}
                    className="input"
                    placeholder="e.g., 0.750"
                  />
                  {errors.length_inches && (<p className="text-red-500 text-sm mt-1">{errors.length_inches.message}</p>)}
                </div>

                <div>
                  <label className="label">Caliber (inches)</label>
                  <input
                    type="number"
                    step="0.001"
                    {...register('caliber_inches', { required: 'Caliber is required' })}
                    className="input"
                    placeholder="e.g., 0.357"
                  />
                  {errors.caliber_inches && (<p className="text-red-500 text-sm mt-1">{errors.caliber_inches.message}</p>)}
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register('cannelured')}
                    className="checkbox"
                  />
                  <label className="label">Cannelured</label>
                </div>

                {cannelured && (
                  <div>
                    <label className="label">Cannelure Depth (inches)</label>
                    <input
                      type="number"
                      step="0.001"
                      {...register('cannelure_depth', { required: 'Cannelure depth is required when cannelured' })}
                      className="input"
                      placeholder="e.g., 0.010"
                    />
                    {errors.cannelure_depth && (<p className="text-red-500 text-sm mt-1">{errors.cannelure_depth.message}</p>)}
                  </div>
                )}

                <div>
                  <label className="label">Core Material</label>
                  <select {...register('core_material', { required: 'Core material is required' })} className="input">
                    <option value="">Select material...</option>
                    {bulletOptions.core_material.map(option => (
                      <option key={option.id} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {errors.core_material && (<p className="text-red-500 text-sm mt-1">{errors.core_material.message}</p>)}
                </div>

                {coreMaterial === 'other' && (
                  <div>
                    <label className="label">Custom Core Material</label>
                    <input
                      type="text"
                      {...register('core_material_other')}
                      className="input"
                      placeholder="Enter custom material..."
                    />
                  </div>
                )}

                <div>
                  <label className="label">Covering</label>
                  <select {...register('covering')} className="input">
                    <option value="">None</option>
                    {bulletOptions.covering.map(option => (
                      <option key={option.id} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                </div>

                {covering === 'other' && (
                  <div>
                    <label className="label">Custom Covering</label>
                    <input
                      type="text"
                      {...register('covering_other')}
                      className="input"
                      placeholder="Enter custom covering..."
                    />
                  </div>
                )}

                <div>
                  <label className="label">Shape</label>
                  <select {...register('shape', { required: 'Shape is required' })} className="input">
                    <option value="">Select shape...</option>
                    {bulletOptions.shape.map(option => (
                      <option key={option.id} value={option.value}>
                        {option.value}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {errors.shape && (<p className="text-red-500 text-sm mt-1">{errors.shape.message}</p>)}
                </div>

                {shape === 'other' && (
                  <div>
                    <label className="label">Custom Shape</label>
                    <input
                      type="text"
                      {...register('shape_other')}
                      className="input"
                      placeholder="Enter custom shape..."
                    />
                  </div>
                )}

                <div className="md:col-span-2" ref={intendedRef}>
                  <label className="label">Intended Cartridges</label>
                  <div className="relative mt-2">
                    <button type="button" className="input w-full flex items-center justify-between" onClick={() => setShowIntendedDropdown(v => !v)}>
                      <span>{intendedCartridges.length > 0 ? `${intendedCartridges.length} selected` : 'Select cartridges...'}</span>
                      <span className="text-gray-400">▾</span>
                    </button>
                    {showIntendedDropdown && (
                      <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow max-h-64 overflow-auto">
                        <div className="p-2 border-b border-gray-100">
                          <input className="input w-full" placeholder="Filter..." value={secondaryFilter} onChange={(e)=>setSecondaryFilter(e.target.value)} />
                        </div>
                        {(() => {
                          const f = (secondaryFilter || '').toLowerCase();
                          const opts = (bulletOptions.cartridge || []);
                          const list = f ? opts.filter(o => ((o?.value || '').toLowerCase().includes(f))) : opts;
                          return list.map(option => (
                            <label key={option.value} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                              <input type="checkbox" className="checkbox" checked={intendedCartridges.includes(option.value)} onChange={() => toggleIntended(option.value)} />
                              <span className="ml-2 text-sm">{option.value}</span>
                      </label>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                  {/* RHF validation hook: require at least one intended cartridge */}
                  <input type="hidden" {...register('intended_cartridges', { validate: () => (Array.isArray(intendedCartridges) && intendedCartridges.length > 0) || 'Select at least one intended cartridge' })} />
                  {errors.intended_cartridges && (<p className="text-red-500 text-sm mt-1">{errors.intended_cartridges.message}</p>)}
                  {/* Preview chips */}
                  {intendedCartridges.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {intendedCartridges.map((v, idx) => (
                        <span key={`preview-top-${idx}`} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs border">{v}</span>
                    ))}
                  </div>
                  )}
                  <div className="mt-2">
                    <input
                      type="text"
                      {...register('custom_cartridge')}
                      className="input"
                      placeholder="Add custom cartridge..."
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        const name = (customCartridge || '').trim();
                        if (!name) return;
                        const newOption = await addBulletOption('cartridge', name);
                          if (newOption) {
                          // merge into options immediately
                          setBulletOptions(prev => {
                            const list = prev.cartridge || [];
                            if (!list.find(o => (o.value || '').toLowerCase() === newOption.value.toLowerCase())) {
                              return { ...prev, cartridge: [...list, { id: newOption.id, value: newOption.value }] };
                            }
                            return prev;
                          });
                          // select it for this bullet
                          const current = Array.isArray(getValues().intended_cartridges) ? getValues().intended_cartridges : [];
                          if (!current.includes(newOption.value)) setValue('intended_cartridges', [...current, newOption.value]);
                            setValue('custom_cartridge', '');
                          toast.success('Custom cartridge added');
                        }
                      }}
                      className="btn btn-secondary mt-2"
                    >
                      Add Custom Cartridge
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="md:col-span-2 flex space-x-3">
              <button type="submit" className="btn btn-primary">
                {editingComponent ? 'Update Component' : 'Add Component'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Component Type Summary */}
      {(getComponentsByType('brass').length > 0 || 
        getComponentsByType('bullet').length > 0 || 
        getComponentsByType('primer').length > 0 || 
        getComponentsByType('powder').length > 0) && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Component Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {componentTypes.map(type => {
              const count = getComponentsByType(type.name.toLowerCase()).length;
              return (
                <div key={type.id} className="text-center">
                  <div className="text-2xl mb-2">{getTypeIcon(type.name)}</div>
                  <div className="text-2xl font-bold text-gray-900">{count}</div>
                  <div className="text-sm text-gray-600 capitalize">{type.name}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Components List with Subheadings */}
      <div className="space-y-8">
        {/* Collapse/Expand All Buttons */}
        <div className="flex items-center gap-2">
          <button onClick={collapseAllCategories} className="btn btn-secondary btn-sm">
            Collapse All
          </button>
          <button onClick={expandAllCategories} className="btn btn-secondary btn-sm">
            Expand All
          </button>
        </div>

        {/* Brass Components */}
        {getComponentsByType('brass').length > 0 && (
          <div>
            <div
              className="flex items-center justify-between mb-4 cursor-pointer select-none hover:bg-gray-100 p-2 rounded -mx-2"
              onClick={() => toggleCategory('brass')}
            >
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                {collapsedCategories.brass ? <ChevronRight className="h-6 w-6 mr-2" /> : <ChevronDown className="h-6 w-6 mr-2" />}
                🥉 Brass Components ({getComponentsByType('brass').length})
              </h2>
              {!collapsedCategories.brass && (
                <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={sortBy.brass}
                    onChange={(e) => handleSortChange('brass', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="remaining">Sort by Remaining</option>
                    <option value="total_purchased">Sort by Total Purchased</option>
                    <option value="total_spent">Sort by Total Spent</option>
                    <option value="brand">Sort by Brand</option>
                    <option value="caliber">Sort by Caliber</option>
                  </select>
                </div>
              )}
            </div>
            {!collapsedCategories.brass && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getComponentsByType('brass').map(component => {
                const amortizationMap = computeBrassAmortization(component.id, purchases, caseBatches, components);
                return (
                  <div
                    key={component.id}
                    className="card p-6 cursor-pointer hover:ring-2 hover:ring-primary-200"
                    onClick={() => openComponentModal(component)}
                  >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="text-2xl">
                  {getTypeIcon(component.component_type.name)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {component.brand} {component.caliber}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {component.component_type.name.toLowerCase() === 'brass' ? (component.head_stamp || component.model || '') : (component.caliber || '')}
                  </p>
                </div>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(component.component_type.name)}`}>
                {component.component_type.name}
              </span>
            </div>
            
                {/* Thumbnails */}
                {Array.isArray(imagesByComponent[component.id]) && imagesByComponent[component.id].length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {imagesByComponent[component.id].map(file => {
                        const src = file.download_url || file.url;
                        const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                        const thumb = isImg ? src : (file.thumbnail_url || src);
                        const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                        return (
                          <div key={file.id} className="flex-shrink-0" onClick={(e) => { e.stopPropagation(); setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url }); }}>
                            {isPdf ? (
                              <PdfThumbnail
                                fileId={file.id}
                                url={src}
                                thumbnailUrl={file.thumbnail_url}
                                filename={file.filename}
                                sizeClass="w-16 h-16"
                              />
                            ) : (
                              <img src={thumb} alt={file.filename} className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
           

            {component.description && component.component_type.name.toLowerCase() !== 'brass' && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-sm text-gray-600">{component.description}</p>
              </div>
            )}
            
            {/* Component-specific information */}
            {component.component_type.name.toLowerCase() === 'bullet' && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {component.weight_grains && (
                    <div>
                      <span className="font-medium">Weight:</span> {component.weight_grains} gr
                    </div>
                  )}
                  {component.length_inches && (
                    <div>
                      <span className="font-medium">Length:</span> {component.length_inches}" 
                    </div>
                  )}
                  {component.caliber_inches && (
                    <div>
                      <span className="font-medium">Caliber:</span> {component.caliber_inches}"
                    </div>
                  )}
                  {component.core_material && (
                    <div>
                      <span className="font-medium">Core:</span> {component.core_material}
                    </div>
                  )}
                  {component.covering && (
                    <div>
                      <span className="font-medium">Covering:</span> {component.covering}
                    </div>
                  )}
                  {component.shape && (
                    <div>
                      <span className="font-medium">Shape:</span> {component.shape}
                    </div>
                  )}
                  {component.cannelured && (
                    <div>
                      <span className="font-medium">Cannelured:</span> Yes
                      {component.cannelure_depth && ` (${component.cannelure_depth}")`}
                    </div>
                  )}
                </div>
                {component.intended_cartridges && (
                  <div className="mt-2">
                    <span className="font-medium text-sm">Cartridges:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {JSON.parse(component.intended_cartridges).map((cartridge, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {cartridge}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Brass-specific information */}
            {component.component_type.name.toLowerCase() === 'brass' && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                {/* Head stamp shown under title already; omit duplicate here */}
                {(component.primer_pocket_size || component.primer_pocket_type) && (
                  <div className="text-sm mb-2">
                    <span className="font-medium">Primer Pocket:</span> {component.primer_pocket_size || '—'}{component.primer_pocket_size === 'Large' && component.primer_pocket_type ? ` • ${component.primer_pocket_type}` : ''}
                </div>
                )}
                {component.description && (
                  <div className="text-sm text-gray-600 mb-2">{component.description}</div>
                )}
                
                {/* Case Batches */}
                {caseBatches[component.id] && caseBatches[component.id].length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-gray-900 mb-2">Case Batches:</div>
                    <div className="space-y-2">
                      {(() => {
                        const merged = {};
                        (caseBatches[component.id] || []).forEach(b => {
                          const key = String(b.firing_count || 0);
                          const totalQty = Number(b.quantity ?? b.remaining_quantity ?? 0);
                          const remainingQty = Number(b.remaining_quantity ?? 0);
                          const firingCount = Number(b.firing_count || 0);
                          const amortizedTarget = Math.max(0, Number(b.amortized_firings || 0));
                          const firingsLeft = Math.max(0, amortizedTarget - firingCount);
                          const cost = Number(b.cost_per_case || 0);
                          if (!merged[key]) {
                            merged[key] = {
                              ...b,
                              quantity: totalQty,
                              remaining_quantity: remainingQty,
                              _weightSum: totalQty,
                              _remainingContribution: firingsLeft * totalQty,
                              _costContribution: cost * totalQty
                            };
                          } else {
                            merged[key].quantity = (merged[key].quantity || 0) + totalQty;
                            merged[key].remaining_quantity = (merged[key].remaining_quantity || 0) + remainingQty;
                            merged[key]._weightSum = (merged[key]._weightSum || 0) + totalQty;
                            merged[key]._remainingContribution = (merged[key]._remainingContribution || 0) + (firingsLeft * totalQty);
                            merged[key]._costContribution = (merged[key]._costContribution || 0) + (cost * totalQty);
                          }
                        });
                        return Object.values(merged)
                          .sort((a, b) => (a.firing_count || 0) - (b.firing_count || 0))
                          .map(batch => {
                            const amortData = amortizationMap[batch.firing_count] || null;
                            const avgCostPerCase = amortData ? amortData.avgCost : Number(batch.cost_per_case || 0);
                            const firingsLeft = amortData ? amortData.firingsLeft : Math.max(0, Math.round((Number(batch.amortized_firings || 0) - Number(batch.firing_count || 0))));
                            const isAmortized = amortData ? amortData.isAmortized : (Number(batch.firing_count || 0) >= Number(batch.amortized_firings || 0));
                            const firingsRemainingText = isAmortized
                              ? 'Fully amortized - $0.00 per use'
                              : `${firingsLeft} more firing${firingsLeft === 1 ? '' : 's'} to amortize`;
                            return (
                              <div key={`fc-${batch.firing_count}`} className="bg-gray-50 p-2 rounded text-xs">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">
                                    {batch.firing_count} firing{batch.firing_count !== 1 ? 's' : ''}
                                  </span>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    isAmortized ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {isAmortized ? 'Amortized' : 'Paying off'}
                                  </span>
                                </div>
                                <div className="flex justify-between text-gray-600 mt-1">
                                  <span>Available: {batch.remaining_quantity}</span>
                                  <span>Cost: ${avgCostPerCase.toFixed(2)}/case</span>
                                </div>
                                <div className="text-gray-500 mt-1">
                                  {firingsRemainingText}
                                </div>
                              </div>
                            );
                          });
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Primer-specific information */}
            {component.component_type.name.toLowerCase() === 'primer' && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {component.firearm_type && (
                    <div>
                      <span className="font-medium">Firearm:</span> {component.firearm_type}
                    </div>
                  )}
                  {component.primer_size && (
                    <div>
                      <span className="font-medium">Size:</span> {component.primer_size}
                    </div>
                  )}
                  {component.magnum && (
                    <div>
                      <span className="font-medium">Magnum:</span> Yes
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Purchase Statistics */}
            {(() => {
              const stats = getComponentStats(component.id);
              return stats.hasPurchases ? (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center">
                      <div className="font-medium text-gray-900">
                        {stats.totalQuantity.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Total Purchased</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium text-gray-900">
                        ${stats.totalSpent.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">Total Spent</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-medium ${stats.totalRemaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stats.totalRemaining.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Remaining</div>
                    </div>
                  </div>
                </div>
              ) : null;
            })()}
            
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Added {formatLocalDate(component.created_at)}
              </div>
              <div className="flex items-center gap-2">
              <button
                  className="btn btn-sm btn-secondary"
                  title="Purchase"
                  onClick={(e) => { e.stopPropagation(); setSelectedComponent(component); setShowPurchaseModal(true); setPurchaseForm({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' }); }}
              >
                  <ShoppingCart className="h-4 w-4 text-white" />
              </button>
                <button
                  className="btn btn-sm btn-secondary"
                  title="Edit"
                  onClick={(e) => { e.stopPropagation(); handleEdit(component); }}
                >
                  <Edit className="h-4 w-4" />
                </button>
                {canDeleteComponent(component) && (
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={(e) => { e.stopPropagation(); deleteComponent(component); }}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}

        {/* Bullet Components */}
        {getComponentsByType('bullet').length > 0 && (
          <div>
            <div
              className="flex items-center justify-between mb-4 cursor-pointer select-none hover:bg-gray-100 p-2 rounded -mx-2"
              onClick={() => toggleCategory('bullet')}
            >
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                {collapsedCategories.bullet ? <ChevronRight className="h-6 w-6 mr-2" /> : <ChevronDown className="h-6 w-6 mr-2" />}
                🎯 Bullet Components ({getComponentsByType('bullet').length})
              </h2>
              {!collapsedCategories.bullet && (
                <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={sortBy.bullet}
                    onChange={(e) => handleSortChange('bullet', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="remaining">Sort by Remaining</option>
                    <option value="total_purchased">Sort by Total Purchased</option>
                    <option value="total_spent">Sort by Total Spent</option>
                    <option value="brand">Sort by Brand</option>
                    <option value="caliber">Sort by Caliber</option>
                  </select>
                </div>
              )}
            </div>
            {!collapsedCategories.bullet && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getComponentsByType('bullet').map(component => (
                <div key={component.id} className="card p-6 cursor-pointer hover:ring-2 hover:ring-primary-200" onClick={() => openComponentModal(component)}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {getTypeIcon(component.component_type.name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {component.brand} {component.model}
                          {component.weight_grains ? ` ${component.weight_grains}gr` : ''}
                          {component.core_material ? ` ${component.core_material}` : ''}
                          {component.covering && String(component.covering).toLowerCase() !== 'none' ? ` ${abbreviateMultiWord(component.covering)}` : ''}
                          {component.shape ? ` ${abbreviateMultiWord(component.shape)}` : ''}
                        </h3>
                        <p className="text-sm text-gray-600">{cartridgeAbbrev(component.caliber)}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(component.component_type.name)}`}>
                      {component.component_type.name}
                    </span>
                  </div>
                
                {/* Thumbnails */}
                {Array.isArray(imagesByComponent[component.id]) && imagesByComponent[component.id].length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {imagesByComponent[component.id].map(file => {
                        const src = file.download_url || file.url;
                        const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                        const thumb = isImg ? src : (file.thumbnail_url || src);
                        const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                        return (
                          <div key={file.id} className="flex-shrink-0" onClick={(e) => { e.stopPropagation(); setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url }); }}>
                            {isPdf ? (
                              <PdfThumbnail
                                fileId={file.id}
                                url={src}
                                thumbnailUrl={file.thumbnail_url}
                                filename={file.filename}
                                sizeClass="w-16 h-16"
                              />
                            ) : (
                              <img src={thumb} alt={file.filename} className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                  
                  {component.description && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{component.description}</p>
                    </div>
                  )}
                  
                  {/* Component-specific information */}
                  {component.component_type.name.toLowerCase() === 'bullet' && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {component.weight_grains && (
                          <div>
                            <span className="font-medium">Weight:</span> {component.weight_grains} gr
                          </div>
                        )}
                        {component.length_inches && (
                          <div>
                            <span className="font-medium">Length:</span> {component.length_inches}" 
                          </div>
                        )}
                        {component.caliber_inches && (
                          <div>
                            <span className="font-medium">Caliber:</span> {component.caliber_inches}"
                          </div>
                        )}
                        {component.core_material && (
                          <div>
                            <span className="font-medium">Core:</span> {component.core_material}
                          </div>
                        )}
                        {component.covering && (
                          <div>
                            <span className="font-medium">Covering:</span> {component.covering}
                          </div>
                        )}
                        {component.shape && (
                          <div>
                            <span className="font-medium">Shape:</span> {component.shape}
                          </div>
                        )}
                        {component.cannelured && (
                          <div>
                            <span className="font-medium">Cannelured:</span> Yes
                            {component.cannelure_depth && ` (${component.cannelure_depth}")`}
                          </div>
                        )}
                      </div>
                      {component.intended_cartridges && (
                        <div className="mt-2">
                          <span className="font-medium text-sm">Cartridges:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {JSON.parse(component.intended_cartridges).map((cartridge, index) => (
                              <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {cartridge}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Purchase Statistics */}
                  {(() => {
                    const stats = getComponentStats(component.id);
                    return stats.hasPurchases ? (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="text-center">
                            <div className="font-medium text-gray-900">
                              {stats.totalQuantity.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Total Purchased</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-gray-900">
                              ${stats.totalSpent.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">Total Spent</div>
                          </div>
                          <div className="text-center">
                            <div className={`font-medium ${stats.totalRemaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stats.totalRemaining.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Remaining</div>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      Added {formatLocalDate(component.created_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        title="Purchase"
                        onClick={(e) => { e.stopPropagation(); setSelectedComponent(component); setShowPurchaseModal(true); setPurchaseForm({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' }); }}
                      >
                        <ShoppingCart className="h-4 w-4 text-white" />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); handleEdit(component); }}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {canDeleteComponent(component) && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={(e) => { e.stopPropagation(); deleteComponent(component); }}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        )}

        {/* Primer Components */}
        {getComponentsByType('primer').length > 0 && (
          <div>
            <div
              className="flex items-center justify-between mb-4 cursor-pointer select-none hover:bg-gray-100 p-2 rounded -mx-2"
              onClick={() => toggleCategory('primer')}
            >
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                {collapsedCategories.primer ? <ChevronRight className="h-6 w-6 mr-2" /> : <ChevronDown className="h-6 w-6 mr-2" />}
                🔥 Primer Components ({getComponentsByType('primer').length})
              </h2>
              {!collapsedCategories.primer && (
                <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={sortBy.primer}
                    onChange={(e) => handleSortChange('primer', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="remaining">Sort by Remaining</option>
                    <option value="total_purchased">Sort by Total Purchased</option>
                    <option value="total_spent">Sort by Total Spent</option>
                    <option value="brand">Sort by Brand</option>
                    <option value="caliber">Sort by Caliber</option>
                  </select>
                </div>
              )}
            </div>
            {!collapsedCategories.primer && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getComponentsByType('primer').map(component => (
                <div key={component.id} className="card p-6 cursor-pointer hover:ring-2 hover:ring-primary-200" onClick={() => openComponentModal(component)}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {getTypeIcon(component.component_type.name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {component.brand} {component.model}
                          {component.weight_grains ? `, ${component.weight_grains}gr` : ''}
                          {component.core_material ? `, ${component.core_material}` : ''}
                          {component.covering && String(component.covering).toLowerCase() !== 'none' ? `, ${abbreviateMultiWord(component.covering)}` : ''}
                          {component.shape ? `, ${abbreviateMultiWord(component.shape)}` : ''}
                        </h3>
                        <p className="text-sm text-gray-600">{component.caliber}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(component.component_type.name)}`}>
                      {component.component_type.name}
                    </span>
                  </div>
                
                {/* Thumbnails */}
                {Array.isArray(imagesByComponent[component.id]) && imagesByComponent[component.id].length > 0 && (
                  <div className="mt-2">
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {imagesByComponent[component.id].map(file => {
                        const src = file.download_url || file.url;
                        const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                        const thumb = isImg ? src : (file.thumbnail_url || src);
                        const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                        return (
                          <div key={file.id} className="flex-shrink-0" onClick={(e) => { e.stopPropagation(); setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url }); }}>
                            {isPdf ? (
                              <PdfThumbnail
                                fileId={file.id}
                                url={src}
                                thumbnailUrl={file.thumbnail_url}
                                filename={file.filename}
                                sizeClass="w-16 h-16"
                              />
                            ) : (
                              <img src={thumb} alt={file.filename} className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                  
                  {component.description && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{component.description}</p>
                    </div>
                  )}
                  
                  {/* Primer-specific information */}
                  {component.component_type.name.toLowerCase() === 'primer' && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {component.firearm_type && (
                          <div>
                            <span className="font-medium">Type:</span> {component.firearm_type}
                          </div>
                        )}
                        {component.primer_size && (
                          <div>
                            <span className="font-medium">Size:</span> {component.primer_size}
                          </div>
                        )}
                        {component.magnum && (
                          <div>
                            <span className="font-medium">Magnum:</span> Yes
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Purchase Statistics */}
                  {(() => {
                    const stats = getComponentStats(component.id);
                    return stats.hasPurchases ? (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="text-center">
                            <div className="font-medium text-gray-900">
                              {stats.totalQuantity.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Total Purchased</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-gray-900">
                              ${stats.totalSpent.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">Total Spent</div>
                          </div>
                          <div className="text-center">
                            <div className={`font-medium ${stats.totalRemaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stats.totalRemaining.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Remaining</div>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      Added {formatLocalDate(component.created_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        title="Purchase"
                        onClick={(e) => { e.stopPropagation(); setSelectedComponent(component); setShowPurchaseModal(true); setPurchaseForm({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' }); }}
                      >
                        <ShoppingCart className="h-4 w-4 text-white" />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); handleEdit(component); }}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {canDeleteComponent(component) && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={(e) => { e.stopPropagation(); deleteComponent(component); }}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        )}

        {/* Powder Components */}
        {getComponentsByType('powder').length > 0 && (
          <div>
            <div
              className="flex items-center justify-between mb-4 cursor-pointer select-none hover:bg-gray-100 p-2 rounded -mx-2"
              onClick={() => toggleCategory('powder')}
            >
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                {collapsedCategories.powder ? <ChevronRight className="h-6 w-6 mr-2" /> : <ChevronDown className="h-6 w-6 mr-2" />}
                💨 Powder Components ({getComponentsByType('powder').length})
              </h2>
              {!collapsedCategories.powder && (
                <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={sortBy.powder}
                    onChange={(e) => handleSortChange('powder', e.target.value)}
                    className="input text-sm"
                  >
                    <option value="remaining">Sort by Remaining</option>
                    <option value="total_purchased">Sort by Total Purchased</option>
                    <option value="total_spent">Sort by Total Spent</option>
                    <option value="brand">Sort by Brand</option>
                    <option value="caliber">Sort by Caliber</option>
                  </select>
                </div>
              )}
            </div>
            {!collapsedCategories.powder && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {getComponentsByType('powder').map(component => (
                <div key={component.id} className="card p-6 cursor-pointer hover:ring-2 hover:ring-primary-200" onClick={() => openComponentModal(component)}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="text-2xl">
                        {getTypeIcon(component.component_type.name)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {component.brand} {component.model}
                          {component.weight_grains ? `, ${component.weight_grains}gr` : ''}
                          {component.core_material ? `, ${component.core_material}` : ''}
                          {component.covering && String(component.covering).toLowerCase() !== 'none' ? `, ${abbreviateMultiWord(component.covering)}` : ''}
                          {component.shape ? `, ${abbreviateMultiWord(component.shape)}` : ''}
                        </h3>
                        <p className="text-sm text-gray-600">{component.caliber}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(component.component_type.name)}`}>
                      {component.component_type.name}
                    </span>
                  </div>
                  
                  {/* Thumbnails */}
                  {Array.isArray(imagesByComponent[component.id]) && imagesByComponent[component.id].length > 0 && (
                    <div className="mt-2">
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {imagesByComponent[component.id].map(file => {
                          const src = file.download_url || file.url;
                          const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((file.filename || ''));
                          const thumb = isImg ? src : (file.thumbnail_url || src);
                          const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                          return (
                            <div key={file.id} className="flex-shrink-0" onClick={(e) => { e.stopPropagation(); setFilePreview({ id: file.id, url: src, filename: file.filename, thumbnailUrl: file.thumbnail_url }); }}>
                              {isPdf ? (
                                <PdfThumbnail
                                  fileId={file.id}
                                  url={src}
                                  thumbnailUrl={file.thumbnail_url}
                                  filename={file.filename}
                                  sizeClass="w-16 h-16"
                                />
                              ) : (
                                <img src={thumb} alt={file.filename} className="w-16 h-16 object-contain bg-white rounded shadow cursor-pointer" />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {component.description && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600">{component.description}</p>
                    </div>
                  )}
                  
                  {/* Purchase Statistics */}
                  {(() => {
                    const stats = getComponentStats(component.id);
                    return stats.hasPurchases ? (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div className="text-center">
                            <div className="font-medium text-gray-900">
                              {stats.totalQuantity.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Total Purchased</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-gray-900">
                              ${stats.totalSpent.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">Total Spent</div>
                          </div>
                          <div className="text-center">
                            <div className={`font-medium ${stats.totalRemaining > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {stats.totalRemaining.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Remaining</div>
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })()}
                  
                  <div className="mt-4 flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      Added {formatLocalDate(component.created_at)}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="btn btn-sm btn-secondary"
                        title="Purchase"
                        onClick={(e) => { e.stopPropagation(); setSelectedComponent(component); setShowPurchaseModal(true); setPurchaseForm({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' }); }}
                      >
                        <ShoppingCart className="h-4 w-4 text-white" />
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        title="Edit"
                        onClick={(e) => { e.stopPropagation(); handleEdit(component); }}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {canDeleteComponent(component) && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={(e) => { e.stopPropagation(); deleteComponent(component); }}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        )}
        
        {/* No Components Message */}
        {getComponentsByType('brass').length === 0 && 
         getComponentsByType('bullet').length === 0 && 
         getComponentsByType('primer').length === 0 && 
         getComponentsByType('powder').length === 0 && (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm || filterType ? 'No components match your filters' : 'No components added yet'}
            </p>
            {!searchTerm && !filterType && (
              <button
                onClick={() => { setEditingComponent(null); try { reset({}); } catch {}; setShowForm(true); }}
                className="btn btn-primary mt-4"
              >
                Add Your First Component
              </button>
            )}
          </div>
        )}
      </div>

      {/* Global file preview modal */}
      <ImagePreviewModal id={filePreview?.id} url={filePreview?.url} filename={filePreview?.filename} thumbnailUrl={filePreview?.thumbnailUrl} onClose={() => setFilePreview(null)} />

      {/* Component Details Modal */}
      {showComponentModal && selectedComponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={closeComponentModal}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {selectedComponent.brand} {selectedComponent.component_type?.name?.toLowerCase() === 'brass' ? selectedComponent.caliber : selectedComponent.model}
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedComponent.component_type?.name?.toLowerCase() === 'brass' ? (selectedComponent.head_stamp || selectedComponent.model || '') : (selectedComponent.caliber || '')}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="btn btn-secondary btn-sm mr-2"
                  title="Purchase"
                  onClick={() => { setShowPurchaseModal(true); setPurchaseForm({ quantity: '', total_cost: '', unit_cost: '', purchase_date: new Date().toISOString().split('T')[0], purchase_location: '', website: '', notes: '' }); }}
                >
                  <ShoppingCart className="h-4 w-4 text-white" />
                </button>
                {canDeleteComponent(selectedComponent) && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => deleteComponent(selectedComponent)}
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { startModalEdit(selectedComponent); }}
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button className="text-gray-400 hover:text-gray-600" onClick={closeComponentModal}>×</button>
              </div>
            </div>
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {modalEditing ? (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Edit Component</h4>
                  <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="label">Component Type</label>
                      <select
                        {...register('component_type_id', { required: 'Component type is required' })}
                        className="input"
                        disabled
                      >
                        <option value={selectedComponent.component_type.id}>{selectedComponent.component_type.name}</option>
                      </select>
                    </div>
                    <div>
                      <label className="label">Brand</label>
                      <input type="text" {...register('brand', { required: 'Brand is required' })} className="input" />
                      {errors.brand && <p className="text-red-500 text-sm mt-1">{errors.brand.message}</p>}
                    </div>
                    {selectedComponent.component_type.name.toLowerCase() === 'brass' ? (
                      <div>
                        <label className="label">Head Stamp</label>
                        <input type="text" {...register('head_stamp', { required: 'Head stamp is required' })} className="input" />
                        {errors.head_stamp && <p className="text-red-500 text-sm mt-1">{errors.head_stamp.message}</p>}
                      </div>
                    ) : (
                      <div>
                        <label className="label">Model</label>
                        <input type="text" {...register('model', { required: 'Model is required' })} className="input" />
                        {errors.model && <p className="text-red-500 text-sm mt-1">{errors.model.message}</p>}
                      </div>
                    )}
                    {selectedComponent.component_type.name.toLowerCase() === 'brass' ? (
                      <div>
                        <label className="label">Cartridge</label>
                        <div className="flex gap-2">
                          <select {...register('caliber', { required: 'Cartridge is required' })} className="input flex-1">
                            <option value="">Select cartridge...</option>
                            {bulletOptions.cartridge.map(option => (
                              <option key={option.id} value={option.value}>{option.value}</option>
                            ))}
                          </select>
                          {!showBrassOtherEdit ? (
                            <button type="button" className="btn btn-secondary" onClick={() => setShowBrassOtherEdit(true)}>Other...</button>
                          ) : (
                            <div className="flex gap-2 flex-1">
                              <input className="input flex-1" placeholder="Enter custom cartridge" value={brassOtherValueEdit} onChange={(e) => setBrassOtherValueEdit(e.target.value)} />
                              <button type="button" className="btn btn-secondary" onClick={() => addBrassCustomCartridge(true)}>Add</button>
                            </div>
                          )}
                        </div>
                        {errors.caliber && <p className="text-red-500 text-sm mt-1">{errors.caliber.message}</p>}
                      </div>
                    ) : null}
                    {/* Brass primer pocket controls in modal edit */}
                    {selectedComponent.component_type.name.toLowerCase() === 'brass' && (
                      <>
                        <div>
                          <label className="label">Primer Pocket Size</label>
                          <select {...register('primer_pocket_size')} className="input">
                            <option value="">Select size...</option>
                            <option value="Small">Small</option>
                            <option value="Large">Large</option>
                          </select>
                        </div>
                        {primerPocketSize === 'Large' && (
                          <div>
                            <label className="label">Large Pocket Type</label>
                            <select {...register('primer_pocket_type')} className="input">
                              <option value="">Select type...</option>
                              <option value="Pistol">Pistol</option>
                              <option value="Rifle">Rifle</option>
                            </select>
                          </div>
                        )}
                      </>
                    )}

                    {/* Primer-specific fields in edit modal */}
                    {selectedComponent.component_type.name.toLowerCase() === 'primer' && (
                      <>
                        <div>
                          <label className="label">Firearm Type</label>
                          <select {...register('firearm_type', { required: 'Firearm type is required' })} className="input">
                            <option value="">Select firearm type...</option>
                            <option value="Pistol">Pistol</option>
                            <option value="Rifle">Rifle</option>
                          </select>
                          {errors.firearm_type && (
                            <p className="text-red-500 text-sm mt-1">{errors.firearm_type.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="label">Primer Size</label>
                          <select {...register('primer_size', { required: 'Primer size is required' })} className="input">
                            <option value="">Select size...</option>
                            <option value="Small">Small</option>
                            <option value="Large">Large</option>
                          </select>
                          {errors.primer_size && (
                            <p className="text-red-500 text-sm mt-1">{errors.primer_size.message}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <input type="checkbox" {...register('magnum')} className="checkbox" />
                          <label className="label">Magnum</label>
                        </div>
                      </>
                    )}

                    {/* Bullet-specific fields in edit modal */}
                    {selectedComponent.component_type.name.toLowerCase() === 'bullet' && (
                      <>
                        <div>
                          <label className="label">Weight (grains)</label>
                          <input
                            type="number"
                            step="0.1"
                            {...register('weight_grains', { required: 'Weight is required' })}
                            className="input"
                            placeholder="e.g., 158.0"
                          />
                          {errors.weight_grains && (<p className="text-red-500 text-sm mt-1">{errors.weight_grains.message}</p>)}
                        </div>
                        <div>
                          <label className="label">Length (inches)</label>
                          <input
                            type="number"
                            step="0.001"
                            {...register('length_inches', { required: 'Length is required' })}
                            className="input"
                            placeholder="e.g., 0.750"
                          />
                          {errors.length_inches && (<p className="text-red-500 text-sm mt-1">{errors.length_inches.message}</p>)}
                        </div>
                        <div>
                          <label className="label">Caliber (inches)</label>
                          <input
                            type="number"
                            step="0.001"
                            {...register('caliber_inches', { required: 'Caliber is required' })}
                            className="input"
                            placeholder="e.g., 0.357"
                          />
                          {errors.caliber_inches && (<p className="text-red-500 text-sm mt-1">{errors.caliber_inches.message}</p>)}
                        </div>
                        <div className="flex items-center space-x-2">
                          <input type="checkbox" {...register('cannelured')} className="checkbox" />
                          <label className="label">Cannelured</label>
                        </div>
                        {cannelured && (
                          <div>
                            <label className="label">Cannelure Depth (inches)</label>
                            <input
                              type="number"
                              step="0.001"
                              {...register('cannelure_depth', { required: 'Cannelure depth is required when cannelured' })}
                              className="input"
                              placeholder="e.g., 0.010"
                            />
                            {errors.cannelure_depth && (<p className="text-red-500 text-sm mt-1">{errors.cannelure_depth.message}</p>)}
                          </div>
                        )}
                        <div>
                          <label className="label">Core Material</label>
                          <select {...register('core_material', { required: 'Core material is required' })} className="input">
                            <option value="">Select material...</option>
                            {bulletOptions.core_material.map(option => (
                              <option key={option.id} value={option.value}>
                                {option.value}
                              </option>
                            ))}
                            <option value="other">Other</option>
                          </select>
                          {errors.core_material && (<p className="text-red-500 text-sm mt-1">{errors.core_material.message}</p>)}
                        </div>
                        {coreMaterial === 'other' && (
                          <div>
                            <label className="label">Custom Core Material</label>
                            <input type="text" {...register('core_material_other')} className="input" placeholder="Enter custom material..." />
                          </div>
                        )}
                        <div>
                          <label className="label">Covering</label>
                          <select {...register('covering')} className="input">
                            <option value="">None</option>
                            {bulletOptions.covering.map(option => (
                              <option key={option.id} value={option.value}>
                                {option.value}
                              </option>
                            ))}
                            <option value="other">Other</option>
                          </select>
                        </div>
                        {covering === 'other' && (
                          <div>
                            <label className="label">Custom Covering</label>
                            <input type="text" {...register('covering_other')} className="input" placeholder="Enter custom covering..." />
                          </div>
                        )}
                        <div>
                          <label className="label">Shape</label>
                          <select {...register('shape', { required: 'Shape is required' })} className="input">
                            <option value="">Select shape...</option>
                            {bulletOptions.shape.map(option => (
                              <option key={option.id} value={option.value}>
                                {option.value}
                              </option>
                            ))}
                            <option value="other">Other</option>
                          </select>
                          {errors.shape && (<p className="text-red-500 text-sm mt-1">{errors.shape.message}</p>)}
                        </div>
                        {shape === 'other' && (
                          <div>
                            <label className="label">Custom Shape</label>
                            <input type="text" {...register('shape_other')} className="input" placeholder="Enter custom shape..." />
                          </div>
                        )}
                        <div className="md:col-span-2" ref={intendedRef}>
                          <label className="label">Intended Cartridges</label>
                          <div className="relative mt-2">
                            <button type="button" className="input w-full flex items-center justify-between" onClick={() => setShowIntendedDropdown(v => !v)}>
                              <span>{intendedCartridges.length > 0 ? `${intendedCartridges.length} selected` : 'Select cartridges...'}</span>
                              <span className="text-gray-400">▾</span>
                            </button>
                            {showIntendedDropdown && (
                              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded shadow max-h-64 overflow-auto">
                                <div className="p-2 border-b border-gray-100">
                                  <input className="input w-full" placeholder="Filter..." value={secondaryFilter} onChange={(e)=>setSecondaryFilter(e.target.value)} />
                                </div>
                                {(() => {
                                  const f = (secondaryFilter || '').toLowerCase();
                                  const opts = (bulletOptions.cartridge || []);
                                  const list = f ? opts.filter(o => ((o?.value || '').toLowerCase().includes(f))) : opts;
                                  return list.map(option => (
                                    <label key={option.value} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                      <input type="checkbox" className="checkbox" checked={intendedCartridges.includes(option.value)} onChange={() => toggleIntended(option.value)} />
                                      <span className="ml-2 text-sm">{option.value}</span>
                                    </label>
                                  ));
                                })()}
                              </div>
                            )}
                          </div>
                          {/* Preview chips (single instance below dropdown) */}
                          {intendedCartridges.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {intendedCartridges.map((v, idx) => (
                                <span key={`preview-modal-${idx}`} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs border">{v}</span>
                              ))}
                            </div>
                          )}
                          {/* RHF validation hook: require at least one intended cartridge */}
                          <input type="hidden" {...register('intended_cartridges', { validate: () => (Array.isArray(intendedCartridges) && intendedCartridges.length > 0) || 'Select at least one intended cartridge' })} />
                          {errors.intended_cartridges && (<p className="text-red-500 text-sm mt-1">{errors.intended_cartridges.message}</p>)}
                          <div className="mt-2">
                            <input type="text" {...register('custom_cartridge')} className="input" placeholder="Add custom cartridge..." />
                            <button
                              type="button"
                              onClick={async () => {
                                const name = (customCartridge || '').trim();
                                if (!name) return;
                                const newOption = await addBulletOption('cartridge', name);
                                if (newOption) {
                                  // merge into options immediately
                                  setBulletOptions(prev => {
                                    const list = prev.cartridge || [];
                                    if (!list.find(o => (o.value || '').toLowerCase() === newOption.value.toLowerCase())) {
                                      return { ...prev, cartridge: [...list, { id: newOption.id, value: newOption.value }] };
                                    }
                                    return prev;
                                  });
                                  // select it for this bullet
                                  const current = Array.isArray(getValues().intended_cartridges) ? getValues().intended_cartridges : [];
                                  if (!current.includes(newOption.value)) setValue('intended_cartridges', [...current, newOption.value]);
                                  setValue('custom_cartridge', '');
                                  toast.success('Custom cartridge added');
                                }
                              }}
                              className="btn btn-secondary mt-2"
                            >
                              Add Custom Cartridge
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    <div className="md:col-span-2">
                      <label className="label">Description</label>
                      <textarea {...register('description')} className="input" rows="3" />
                    </div>
                    <div className="md:col-span-2 flex space-x-3">
                      <button type="submit" className="btn btn-primary">Update Component</button>
                      <button type="button" onClick={() => { setModalEditing(false); setEditingComponent(null); reset({}); }} className="btn btn-secondary">Cancel</button>
                    </div>
                  </form>
                </div>
              ) : (
              <>
              {/* Images */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Images</h4>
                <div className="flex items-center gap-3 mb-2">
                  <label className="btn btn-secondary btn-sm cursor-pointer">
                    Upload Files
                    <input type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.rtf,.txt,.csv,.json,.md,.log" multiple className="hidden" onChange={handleUpload} />
                  </label>
                </div>
                {images.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {images.map(img => {
                      const src = img.download_url || img.url;
                      const isPdf = (img.filename || '').toLowerCase().endsWith('.pdf') || (src || '').toLowerCase().endsWith('.pdf');
                      return (
                        <div key={img.id} className="relative group">
                          {isPdf ? (
                            <PdfThumbnail
                              fileId={img.id}
                              url={src}
                              thumbnailUrl={img.thumbnail_url}
                              filename={img.filename}
                              sizeClass="w-20 h-20"
                              onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })}
                            />
                          ) : (
                            (() => {
                              const isImg = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((img.filename || ''));
                              const thumb = isImg ? src : (img.thumbnail_url || src);
                              return (
                                <img src={thumb} alt={img.filename} className="w-20 h-20 object-contain bg-white rounded shadow cursor-pointer" onClick={() => setFilePreview({ id: img.id, url: src, filename: img.filename, thumbnailUrl: img.thumbnail_url })} />
                              );
                            })()
                          )}
                          <button className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full px-2 py-0.5 text-xs opacity-0 group-hover:opacity-100" onClick={() => removeImage(img.id)}>×</button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No images uploaded.</p>
                )}
              </div>

              

              {/* Purchases */}
              <div>
                <button
                  className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3 hover:text-gray-700"
                  onClick={() => setModalSectionsCollapsed(prev => ({ ...prev, purchases: !prev.purchases }))}
                >
                  {modalSectionsCollapsed.purchases ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  Purchases ({getPurchasesForComponent(selectedComponent.id).length})
                </button>
                {!modalSectionsCollapsed.purchases && (
                  getPurchasesForComponent(selectedComponent.id).length > 0 ? (
                    <div className="space-y-3">
                      {getPurchasesForComponent(selectedComponent.id).map(p => (
                        <div key={p.id} className="border border-gray-200 rounded p-3 text-sm flex justify-between">
                          <div className="space-y-1">
                            <div className="text-gray-900 font-medium">{formatLocalDate(p.purchase_date)}</div>
                            <div className="text-gray-600">Qty: {p.quantity.toLocaleString()} {p.unit} • Remaining: {p.remaining_quantity.toLocaleString()} {p.unit}</div>
                            {p.purchase_location && <div className="text-gray-600">Location: {p.purchase_location}</div>}
                            {p.website && <div className="text-blue-600"><a href={p.website} target="_blank" rel="noreferrer">Website</a></div>}
                            {p.notes && <div className="text-gray-600">{p.notes}</div>}
                          </div>
                          <div className="text-right">
                            <div className="text-gray-900 font-semibold">${p.total_cost.toFixed(2)}</div>
                            <div className="text-gray-600">${p.unit_cost.toFixed(2)}/unit</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No purchases found for this component.</p>
                  )
                )}
              </div>

              {/* Brass Failure Removals (only for brass) */}
              {selectedComponent.component_type?.name?.toLowerCase() === 'brass' && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Remove Failed Cases</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
                    <div>
                      <label className="label">Case Batch</label>
                      <select
                        className="input text-sm"
                        value={removalForm.case_batch_id}
                        onChange={(e) => handleRemovalChange('case_batch_id', e.target.value)}
                      >
                        <option value="">Select batch...</option>
                        {(caseBatches[selectedComponent.id] || []).map(cb => (
                          <option key={cb.id} value={cb.id}>
                            {cb.firing_count} firings • {cb.remaining_quantity} avail
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="label">Quantity</label>
                      <input
                        type="number"
                        className="input text-sm"
                        value={removalForm.quantity}
                        onChange={(e) => handleRemovalChange('quantity', e.target.value)}
                        placeholder="e.g., 10"
                      />
                    </div>
                    <div>
                      <label className="label">Date</label>
                      <input
                        type="date"
                        className="input text-sm"
                        value={removalForm.removal_date}
                        onChange={(e) => handleRemovalChange('removal_date', e.target.value)}
                      />
                    </div>
                    <div className="md:col-span-1">
                      <button className="btn btn-primary w-full" onClick={submitRemoval}>Remove</button>
                    </div>
                    <div className="md:col-span-4">
                      <label className="label">Reason (failure type)</label>
                      <input
                        type="text"
                        className="input text-sm"
                        value={removalForm.reason}
                        onChange={(e) => handleRemovalChange('reason', e.target.value)}
                        placeholder="e.g., split neck, cracked case, loose primer pocket"
                      />
                    </div>
                  </div>

                  <h5 className="text-sm font-medium text-gray-900 mt-4 mb-2">Removal History</h5>
                  {removals.length > 0 ? (
                    <div className="space-y-2 text-sm">
                      {removals.map(r => (
                        <div key={r.id} className="border border-gray-200 rounded p-2 flex justify-between">
                          <div>
                            <div className="text-gray-900">{formatLocalDate(r.removal_date)}</div>
                            <div className="text-gray-600">Qty: {r.quantity} {r.reason ? `• ${r.reason}` : ''}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">No removals recorded.</p>
                  )}
                </div>
              )}

              {/* Recipes Using This Component */}
              <div>
                <button
                  className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-3 hover:text-gray-700"
                  onClick={() => setModalSectionsCollapsed(prev => ({ ...prev, recipes: !prev.recipes }))}
                >
                  {modalSectionsCollapsed.recipes ? <ChevronRight className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  Used In Recipes ({getRecipesUsingComponent(selectedComponent.id).length})
                </button>
                {!modalSectionsCollapsed.recipes && (
                  getRecipesUsingComponent(selectedComponent.id).length > 0 ? (
                    <div className="space-y-3">
                      {getRecipesUsingComponent(selectedComponent.id).map(r => (
                        <div
                          key={r.id}
                          className="border border-gray-200 rounded p-3 text-sm cursor-pointer hover:bg-gray-50 hover:border-blue-300 transition-colors"
                          onClick={() => navigate(`/recipes?recipeId=${r.id}`)}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-blue-600 font-medium hover:underline">{r.name}</div>
                              <div className="text-gray-600">{r.cartridge || r.cartridge_other || '—'}</div>
                            </div>
                            {r.firearm && (
                              <div className="text-right text-gray-600">
                                <div>{r.firearm.manufacturer} {r.firearm.model}</div>
                                <div className="text-xs">{r.firearm.caliber}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">This component is not used in any recipes.</p>
                  )
                )}
              </div>
              </>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button className="btn btn-secondary" onClick={closeComponentModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showPurchaseModal && selectedComponent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowPurchaseModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col" onClick={(e)=>e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Record Purchase — {selectedComponent.brand} {selectedComponent.component_type?.name?.toLowerCase()==='brass' ? selectedComponent.caliber : selectedComponent.model}</h3>
              <button className="text-gray-400 hover:text-gray-600" onClick={() => setShowPurchaseModal(false)}>×</button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 flex-1 overflow-y-auto">
              <div>
                <label className="label">Quantity</label>
                <input type="number" className="input" value={purchaseForm.quantity} onChange={(e)=>handlePurchaseCalc('quantity', e.target.value)} placeholder="e.g., 100" />
              </div>
              <div>
                <label className="label">Purchase Date</label>
                <input type="date" className="input" value={purchaseForm.purchase_date} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_date:e.target.value}))} />
              </div>
              <div>
                <label className="label">Total Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.total_cost} onChange={(e)=>handlePurchaseCalc('total_cost', e.target.value)} placeholder="e.g., 50.00" />
              </div>
              <div>
                <label className="label">Unit Cost ($)</label>
                <input type="number" step="0.01" className="input" value={purchaseForm.unit_cost} onChange={(e)=>handlePurchaseCalc('unit_cost', e.target.value)} placeholder="auto" />
              </div>
              {selectedComponent?.component_type?.name?.toLowerCase() === 'brass' && (
                <div className="md:col-span-2">
                  <label className="label">Starting Firing Count (brass)</label>
                  <input type="number" min="0" className="input" value={purchaseForm.starting_firing_count} onChange={(e)=>setPurchaseForm(prev=>({...prev, starting_firing_count:e.target.value}))} placeholder="e.g., 0 for new, 1+ for used cases" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="label">Purchase Location</label>
                <input type="text" className="input" value={purchaseForm.purchase_location} onChange={(e)=>setPurchaseForm(prev=>({...prev, purchase_location:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Website</label>
                <input type="url" className="input" value={purchaseForm.website} onChange={(e)=>setPurchaseForm(prev=>({...prev, website:e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <label className="label">Notes</label>
                <textarea className="input" rows="3" value={purchaseForm.notes} onChange={(e)=>setPurchaseForm(prev=>({...prev, notes:e.target.value}))} />
              </div>
            </div>
            <div className="p-6 pt-0 border-t border-gray-200 flex justify-end gap-2">
              <button className="btn btn-secondary" onClick={()=>setShowPurchaseModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitPurchaseFromComponent}>Save Purchase</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Components;